import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { useAlert } from "../AlertContext";
import { toISODate, formatDateDE } from "../utils";
import {
    getAppointmentSlots,
    getAppointments,
    confirmAppointment,
    cancelAppointment,
    rescheduleAppointment,
} from "../api";

function StatusBadge({ status }) {
    const cls = {
        REQUESTED: "bg-yellow-100 text-yellow-800",
        CONFIRMED: "bg-green-100 text-green-800",
        RESCHEDULED: "bg-blue-100 text-blue-800",
        CANCELLED: "bg-gray-200 text-gray-700",
    }[status] || "bg-gray-100 text-gray-800";

    const label = {
        REQUESTED: "ANGEFRAGT",
        CONFIRMED: "BESTÄTIGT",
        RESCHEDULED: "GEÄNDERT",
        CANCELLED: "ABGESAGT",
    }[status] || status;

    return (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${cls}`}>
      {label}
    </span>
    );
}

function RescheduleModal({
                             open, onClose, onSubmit,
                             initialDate, initialTime,
                             title = "Termin ändern",
                         }) {
    const { showAlert } = useAlert();
    const [date, setDate] = useState(initialDate || "");
    const [time, setTime] = useState(initialTime || "");
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // --- Neu: Helfer für „Vergangenheit“ ---
    const todayStr = toISODate(new Date());
    const now = new Date();
    const nowHHmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const isPastTime = (d, hhmm) => d === todayStr && hhmm < nowHHmm;

    useEffect(() => {
        if (!open) return;
        setDate(initialDate || "");
        setTime(initialTime || "");
    }, [open, initialDate, initialTime]);

    // Slots laden, wenn Datum gewählt
    useEffect(() => {
        if (!open || !date) return;
        setLoading(true);
        setErr("");
        getAppointmentSlots(date)
            .then((data) => setSlots(Array.isArray(data) ? data : []))
            .catch(() => setErr("Slots konnten nicht geladen werden."))
            .finally(() => setLoading(false));
    }, [open, date]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">{title}</h3>

                <div className="space-y-3">
                    <label className="block text-sm font-medium mb-1">Datum</label>
                    <input
                        type="date"
                        value={date}
                        min={todayStr}                    // ❗Vergangene Tage sperren
                        onChange={(e) => {
                            const newDate = e.target.value;
                            const day = new Date(newDate).getDay();
                            if (day === 0 || day === 6) {
                                showAlert("Samstage und Sonntage sind nicht buchbar.");
                                e.target.value = "";
                                setDate("");
                                setTime("");
                                return;
                            }
                            setDate(newDate);
                            setTime(""); // Zeit zurücksetzen beim Datumwechsel
                        }}
                        className="w-full p-2 border rounded"
                    />

                    <label className="block text-sm font-medium mt-4 mb-2">Uhrzeit auswählen</label>

                    {loading ? (
                        <div className="text-sm text-gray-500">Lade Slots…</div>
                    ) : err ? (
                        <div className="text-sm text-red-600">{err}</div>
                    ) : !date ? (
                        <div className="text-sm text-gray-500">Bitte zuerst ein Datum wählen.</div>
                    ) : (
                        <div className="grid grid-cols-3 gap-2">
                            {slots.map((s) => {
                                const label = s.time;            // "HH:mm"
                                const past = isPastTime(date, label);
                                // Belegt, außer es ist exakt der eigene aktuelle Slot (damit unverändert speichern möglich)
                                const taken = s.booked && !(date === initialDate && label === initialTime);
                                const disabled = past || taken;

                                const isSelected = time === label;
                                const cls = disabled
                                    ? (past ? "bg-gray-400 text-white cursor-not-allowed"
                                        : "bg-red-500 text-white cursor-not-allowed")
                                    : isSelected
                                        ? "bg-blue-700 text-white"
                                        : "bg-blue-500 text-white hover:bg-blue-600";

                                const title =
                                    past ? "Zeit liegt in der Vergangenheit"
                                        : taken ? "Bereits belegt"
                                            : isSelected ? "Ausgewählt"
                                                : "";

                                return (
                                    <button
                                        key={label}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => !disabled && setTime(label)}
                                        className={`py-2 px-3 rounded transition ${cls}`}
                                        title={title}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                    <button onClick={onClose} className="px-3 py-2 rounded border text-gray-700">
                        Abbrechen
                    </button>
                    <button
                        onClick={() => {
                            if (!date || !time) return;
                            if (isPastTime(date, time)) return;
                            const day = new Date(date).getDay();
                            if (day === 0 || day === 6) return;
                            onSubmit({ date, time });
                        }}
                        disabled={!date || !time || isPastTime(date, time)}
                        className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        Speichern
                    </button>
                </div>
            </div>
        </div>
    );
}


export default function BookedAppointments() {
    const { token, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const scrollToId = location.state?.scrollToId;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");
    const [rescheduleFor, setRescheduleFor] = useState(null);

    useEffect(() => {
        if (!token) navigate("/login");
    }, [token, navigate]);
    const refresh = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getAppointments(token);
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            if (e.message === "unauthorized") {
                logout();
                navigate("/login");
            } else {
                setError("Konnte Termine nicht laden.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        if (!loading && scrollToId) {
            const el = document.getElementById(`appt-${scrollToId}`);
            el?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [loading, scrollToId]);

    const doConfirm = async (id) => {
        setBusyId(id);
        setError("");
        try {
            const res = await confirmAppointment(token, id);
            if (!res.ok) throw new Error();
            await refresh();
        } catch (e) {
            if (e.message === "unauthorized") {
                logout();
                navigate("/login");
            } else {
                setError("Bestätigung fehlgeschlagen.");
            }
        } finally {
            setBusyId(null);
        }
    };

    const doCancel = async (id) => {
        if (!window.confirm("Diesen Termin wirklich löschen/absagen?")) return;
        setBusyId(id);
        setError("");
        try {
            const res = await cancelAppointment(token, id);
            if (!res.ok) throw new Error();
            await refresh();
        } catch (e) {
            if (e.message === "unauthorized") {
                logout();
                navigate("/login");
            } else {
                setError("Löschen/Absagen fehlgeschlagen.");
            }
        } finally {
            setBusyId(null);
        }
    };

    const doReschedule = async (id, payload) => {
        // einfache Frontend-Validierung
        if (!payload?.date || !payload?.time) return;
        setBusyId(id);
        setError("");
        try {
            const res = await rescheduleAppointment(token, id, payload);
            if (res.status === 409) {
                setError("Neuer Slot bereits vergeben.");
            } else if (!res.ok) {
                throw new Error();
            } else {
                setRescheduleFor(null);
                await refresh();
            }
        } catch (e) {
            if (e.message === "unauthorized") {
                logout();
                navigate("/login");
            } else if (!error) {
                setError("Änderung fehlgeschlagen.");
            }
        } finally {
            setBusyId(null);
        }
    };

    const sorted = useMemo(() => {
        return [...items].sort((a, b) =>
            (a.date + a.time).localeCompare(b.date + b.time)
        );
    }, [items]);

    return (
        <section className="min-h-screen bg-gray-50">
            <h2 className="text-2xl font-bold p-4">Gebuchte Termine</h2>

            {error && (
                <div className="mx-4 mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">
                    {error}
                </div>
            )}

            <div className="overflow-x-auto bg-white rounded-none shadow-none">
                <table className="min-w-full">
                    <thead>
                    <tr className="bg-blue-600 text-white text-left">
                        <th className="py-2 px-3">Kunde</th>
                        <th className="py-2 px-3">E-Mail</th>
                        <th className="py-2 px-3">Telefon</th>
                        <th className="py-2 px-3">KFZ</th>
                        <th className="py-2 px-3">Service</th>
                        <th className="py-2 px-3">Datum</th>
                        <th className="py-2 px-3">Zeit</th>
                        <th className="py-2 px-3">Status</th>
                        <th className="py-2 px-3">Aktionen</th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr>
                            <td className="py-6 px-3 text-gray-500" colSpan={9}>
                                Lädt…
                            </td>
                        </tr>
                    ) : sorted.length === 0 ? (
                        <tr>
                            <td className="py-6 px-3 text-gray-500" colSpan={9}>
                                Keine Termine vorhanden.
                            </td>
                        </tr>
                    ) : (
                        sorted.map((a) => {
                            const highlight = scrollToId === a.id;
                            return (
                                <tr key={a.id} id={`appt-${a.id}`} className={`border-t ${highlight ? 'bg-yellow-100' : ''}`}>
                                    <td className="py-2 px-3">{a.name}</td>
                                    <td className="py-2 px-3">{a.email}</td>
                                    <td className="py-2 px-3">{a.telefon || "-"}</td>
                                    <td className="py-2 px-3">{a.kfz}</td>
                                    <td className="py-2 px-3">{a.service || "-"}</td>
                                    <td className="py-2 px-3">{formatDateDE(a.date)}</td>
                                    <td className="py-2 px-3">{a.time}</td>
                                    <td className="py-2 px-3">
                                        <StatusBadge status={a.status} />
                                    </td>
                                    <td className="py-2 px-3">
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                onClick={() => doConfirm(a.id)}
                                            disabled={busyId === a.id || a.status === "CONFIRMED"}
                                            className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                            title="Bestätigen"
                                        >
                                            Bestätigen
                                        </button>
                                        <button
                                            onClick={() => setRescheduleFor(a)}
                                            disabled={busyId === a.id}
                                            className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                            title="Ändern"
                                        >
                                            Ändern
                                        </button>
                                        <button
                                            onClick={() => doCancel(a.id)}
                                            disabled={busyId === a.id}
                                            className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                            title="Löschen"
                                        >
                                            Löschen
                                        </button>
                                    </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>

            {/* Reschedule Modal mit Slotprüfung */}
            <RescheduleModal
                open={!!rescheduleFor}
                onClose={() => setRescheduleFor(null)}
                initialDate={rescheduleFor?.date}
                initialTime={rescheduleFor?.time}
                onSubmit={({ date, time }) => doReschedule(rescheduleFor.id, { date, time })}
            />
        </section>
    );
}
